<script setup lang="ts">
import type { FormkitCommonProps } from "@/components/ui/input/formkit/formkit.types";
import type { TRefSiteLayoutDto } from "@/types/backoffice/dto/reference/ref-site-layout-dto.ts";
import type { FormKitFrameworkContext } from "@formkit/core";
import type { PropType } from "vue";

import { useQuery } from "@tanstack/vue-query";
import { computed, nextTick, watch } from "vue";

import BackofficeApi from "@/api/backoffice/backoffice-api.ts";
import { ReferenceQueryKeys } from "@/api/backoffice/reference/reference-query-keys.ts";
import { DATA_TEST_IDS } from "@/constants/data-test-ids";
import { useFormkitComputedValue } from "@/hooks/common/use-formkit";
import { groupBy } from "@/lib/collections";
import { useTypedIntl } from "@/locales/intl";
import { isDefined } from "@/types/utils";

/**
 * @deprecated Use non-formkit-specific SiteLayoutSelect instead or its wrapped version SiteLayoutDropdownInput
 */
import CascadeSelect from "primevue/cascadeselect";
import Skeleton from "primevue/skeleton";

export interface FormKitSiteLayoutCascadeSelectProps extends FormkitCommonProps {
  layoutType: "accommodation" | "non-residential";
}

const props = defineProps({
  context: {
    type: Object as PropType<FormKitFrameworkContext & FormKitSiteLayoutCascadeSelectProps>,
  },
});

const { rawT } = useTypedIntl();

const { data: siteTypeRefs, isLoading: isSiteTypeRefsLoading } = useQuery({
  queryKey: ReferenceQueryKeys.siteTypeRef(),
  queryFn: () => BackofficeApi.findAllSiteTypeRef(),
});
const { data: siteLayoutRefs, isLoading: isSiteLayoutRefsLoading } = useQuery({
  queryKey: ReferenceQueryKeys.siteLayoutRef(),
  queryFn: () => BackofficeApi.findAllSiteLayoutRef(),
});

const model = useFormkitComputedValue<TRefSiteLayoutDto | undefined>(props.context);

const siteTypeRef = computed(() => {
  const value = model.value;
  if (!value) {
    return undefined;
  }

  return siteTypeRefs.value?.find((o) => o.id === value.refTypeSiteId);
});

// Reset value if layoutType and siteType are incompatible accomodation-wise
watch(
  () => [props.context?.layoutType, siteTypeRef.value] as const,
  ([layoutType, siteType]) => {
    if (!siteType || !layoutType) {
      return;
    }

    nextTick(() => {
      const shouldResetValue =
        (layoutType === "accommodation" && !siteType.accommodation) ||
        (layoutType === "non-residential" && siteType.accommodation);

      if (shouldResetValue) {
        model.value = undefined;
      }
    });
  },
  { immediate: true, deep: true },
);

const isLoading = computed(() => isSiteTypeRefsLoading.value || isSiteLayoutRefsLoading.value);

const groupedLayouts = computed(() => {
  const siteLayouts = siteLayoutRefs.value;
  const siteTypes = siteTypeRefs.value;
  const value = model.value;

  if (isLoading.value || !siteLayouts || !siteTypes) {
    return value?.translationKey
      ? [
          {
            name: value.description,
            code: value.id,
            layouts: [value],
          },
        ]
      : [];
  }

  const grouped = groupBy(siteLayoutRefs.value, (o) => o.refTypeSiteId);

  return Array.from(grouped)
    .map(([siteTypeId, layouts]) => {
      const siteTypeRef = siteTypeRefs.value.find((o) => o.id === siteTypeId);

      if (!siteTypeRef) {
        return null;
      }

      const shouldExcludeByLayoutType =
        (props.context?.layoutType === "accommodation" && !siteTypeRef.accommodation) ||
        (props.context?.layoutType === "non-residential" && siteTypeRef.accommodation);

      if (shouldExcludeByLayoutType) {
        return null;
      }

      return {
        name: siteTypeRef.translationKey
          ? rawT(`site.siteTypeLayouts.${siteTypeRef.translationKey}`)
          : siteTypeRef.description,
        code: siteTypeRef.id,
        layouts,
      };
    })
    .filter(isDefined);
});

function formatSelectValue(val: TRefSiteLayoutDto): string {
  const siteTypeRef = siteTypeRefs.value?.find((o) => o.id === val.refTypeSiteId);

  if (!siteTypeRef) {
    return val.translationKey ? rawT(`site.siteTypeLayouts.${val.translationKey}`) : val.description;
  }

  return `${
    siteTypeRef.translationKey ? rawT(`site.siteTypeLayouts.${siteTypeRef.translationKey}`) : siteTypeRef.description
  } > ${val.translationKey ? rawT(`site.siteLayouts.${val.translationKey}`) : val.description}`;
}

function formatSelectOption(val: TRefSiteLayoutDto): string {
  return val.translationKey ? rawT(`site.siteLayouts.${val.translationKey}`) : val.description;
}
</script>

<template>
  <Skeleton
    v-if="isLoading"
    class="w-full h-full"
    style="height: 3rem"
    :data-test-id="DATA_TEST_IDS.components.siteLayoutCascadeSelect.skeleton()"
  />
  <CascadeSelect
    v-else
    v-model="model"
    :disabled="props.context?.disabled"
    :loading="isLoading"
    :options="groupedLayouts"
    :name="props.context?.name"
    :option-label="(val: TRefSiteLayoutDto) => formatSelectOption(val)"
    option-group-label="name"
    :option-group-children="['layouts']"
    data-key="id"
    :placeholder="props.context?.placeholder"
    class="w-full"
    v-bind="$attrs"
    :data-test-id="DATA_TEST_IDS.components.siteLayoutCascadeSelect.select()"
  >
    <template #value="{ value: cascadeSelectValue, placeholder: plh }">
      <template v-if="!cascadeSelectValue">
        {{ plh }}
      </template>
      <template v-else>
        {{ formatSelectValue(cascadeSelectValue) }}
      </template>
    </template>
  </CascadeSelect>
</template>

<style scoped></style>
